import type { CenterProps } from '@hover/blueprint';
import {
  Body,
  Center,
  Flex,
  Heading,
  IconButton,
  motion,
  system,
  transition,
} from '@hover/blueprint';
import { iconExternalLink } from '@hover/icons';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import React, { useMemo } from 'react';
import { Trans } from 'react-i18next';

import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { useEffectOnMount } from 'src/shared/hooks';
import { fadePresence } from 'src/shared/utils/motion';

import { useHomeownerTranslate } from '../../hooks';
import { useImperativeTracking } from '../../hooks/useImperativeTracking';
import { usePartnerMaterialCallout } from '../../hooks/usePartner';
import type { DesignerProductCatalogProduct } from '../../types';
import { getDisplayImageTracking } from '../../utils/tracking';
import { AnimatedEllipsis } from '../AnimatedEllipsis';

import { ProductSwatch } from './ProductSwatch';

interface SwatchCalloutProps extends CenterProps {
  region?: string;
  colorScheme?: 'light' | 'dark';
  isLoading?: boolean;
  product: DesignerProductCatalogProduct;
  image?: AiDisplayImageFragment;
}

const MotionAnimatedEllipsis = motion(AnimatedEllipsis);
const MotionFlex = motion(Flex);

export const SwatchCallout: React.FC<SwatchCalloutProps> = ({
  region,
  isLoading = false,
  colorScheme = 'light',
  product,
  image,
  ...props
}) => {
  const t = useHomeownerTranslate();

  const isDark = colorScheme === 'dark';
  const background = isDark ? 'neutral.900' : 'neutral.0';
  const { trackingRef } = useImperativeTracking();

  const { primaryLabel, secondaryLabel, manufacturer } = product;
  const display = usePartnerMaterialCallout();

  const manufacturerLabel = region ? manufacturer : display?.label;

  const productName = secondaryLabel
    ? `${primaryLabel} in ${secondaryLabel}`
    : primaryLabel;

  const materialRegion = region ? primaryLabel : product.palettes[0];

  const trackingData = useMemo(
    () => ({
      ...(image ? getDisplayImageTracking(image) : {}),
      link_text: `${region ?? productName} ${materialRegion}`,
      feature: 'material-callouts',
      primary_cta: false,
      track_id: 'get-quote-link',
      provider_name: manufacturer,
      variation_uuid: product.id,
      destination_url: product.manufacturerUrl,
    }),
    [region, productName, materialRegion, manufacturer, product, image],
  );

  useEffectOnMount(() => {
    if (product.manufacturerUrl) {
      trackingRef.current?.linkImpression(trackingData);
    }
  });

  const handleOnClick = () => {
    trackingRef.current?.linkPressed(trackingData);
  };

  const productLabel = secondaryLabel ? (
    <Trans
      components={{ Bold: <system.span fontWeight="bodyBold" /> }}
      i18nKey="materialCallouts.swatchCallout.primarySecondaryProductLabel"
      t={t}
      values={{ primaryLabel, secondaryLabel }}
    />
  ) : (
    <strong>{primaryLabel}</strong>
  );

  return (
    <Center
      as="a"
      bg={isLoading ? 'neutral.50' : background}
      borderRadius={isDark ? 400 : 0}
      color={isDark ? 'neutral.0' : 'neutral.900'}
      data-group
      display="flex"
      height={500}
      href={product.manufacturerUrl}
      overflow="hidden"
      padding={isDark ? 400 : 0}
      pointerEvents={isLoading ? 'none' : 'auto'}
      position="relative"
      target="_blank"
      textDecoration="none"
      {...transition.easing250('background')}
      {...props}
      onClick={product.manufacturerUrl ? handleOnClick : undefined}
    >
      <MotionConfig transition={{ duration: 0.25 }}>
        <AnimatePresence initial={false}>
          {isLoading ? (
            <MotionAnimatedEllipsis
              color="neutral.900"
              height="25"
              label={t('materialCallouts.calloutButtonLoading')}
              position="absolute"
              {...fadePresence}
            />
          ) : (
            <MotionFlex
              alignItems="center"
              direction="row"
              height="100%"
              key="swatch"
              width="100%"
              {...fadePresence}
            >
              <ProductSwatch
                borderRadius={300}
                height={500}
                product={product}
                width={500}
              />

              <Flex
                alignItems="stretch"
                color={isDark ? 'neutral.0' : 'neutral.900'}
                direction="column"
                flex={1}
                height="100%"
                justifyContent="center"
                pl={300}
              >
                <Heading
                  color="inherit"
                  fontWeight={region ? 'heading' : 'body'}
                  margin={0}
                  noOfLines={1}
                  size="xs"
                >
                  {region ?? productLabel}
                </Heading>
                <Body
                  color="inherit"
                  fontSize={200}
                  fontWeight={700}
                  lineHeight={200}
                  margin={0}
                  noOfLines={1}
                >
                  {materialRegion}
                </Body>
                {manufacturerLabel && (
                  <Body
                    color={isDark ? 'text.secondaryInvert' : 'text.secondary'}
                    m={0}
                    noOfLines={1}
                    size="small"
                  >
                    {manufacturerLabel}
                  </Body>
                )}
              </Flex>
              {product.manufacturerUrl && (
                <IconButton
                  _groupHover={{
                    color: isDark ? 'text.primaryInvert' : 'text.primary',
                  }}
                  colorScheme={isDark ? 'dark' : 'light'}
                  flexShrink={0}
                  icon={iconExternalLink}
                  label="manufacture link"
                  {...transition.easing250('color')}
                />
              )}
            </MotionFlex>
          )}
        </AnimatePresence>
      </MotionConfig>
    </Center>
  );
};
